import React, { FunctionComponent, useState } from 'react'
import { Spinner } from '../../base-components'
import useTranslation from '../../hooks/useTranslation'
import Modal, { ModalClosingButton } from '../modal'
import style from './digitalGiveawayModal.css'
import CopyIcon from '../../assets/media/icons/copy-link.svg'
import DoneIcon from '../../assets/media/icons/check.svg'
import { Input } from '../form'
import QRCode from 'react-qr-code'

export type Props = {
    createDigitalGiveawayUrl: Function
    resetDigitalGiveawayUrl: Function
    openModalState: [boolean, Function]
    isLoading: boolean
    urlLink: string
    dealerIdIsRequired: boolean
    dgaDealerId: string
}

const DigitalGiveawayModal: FunctionComponent<Props> = (props) => {
    const {
        openModalState,
        isLoading,
        createDigitalGiveawayUrl,
        resetDigitalGiveawayUrl,
        urlLink,
        dealerIdIsRequired,
        dgaDealerId,
    } = props
    const { t } = useTranslation()
    const [copied, setCopied] = useState(false)
    const [dealerId, setDealerId] = useState(dgaDealerId || "")
    const [dealerIdError, setDealerIdError] = useState(false)
    const [modalIsOpen, setModalIsOpen] = openModalState

    const closeHandler = (): void => {
        setCopied(false)
        resetDigitalGiveawayUrl()
        setDealerIdError(false)
        setModalIsOpen(false)
    }

    const clickHandler = (flightCamera: boolean): void => {
        if (dealerIdIsRequired && !dealerId) {
            setDealerIdError(true)
        } else {
            setDealerIdError(false)
            createDigitalGiveawayUrl({ flightCamera: flightCamera, dealerId: dealerId })
        }
    }

    const copyClickHandler = (): void => {
        setCopied(true)
        navigator.clipboard.writeText(urlLink)
    }

    const getCopiedClass = (): string => {
        return copied ? style.copied : ""
    }

    const getCopiedIcon = () => {
        return copied ? <DoneIcon className={style.copiedSvg} /> : <CopyIcon />
    }

    const dealerIdClasses = dealerIdError
        ? style.dealerInput + " " + style.dealerInputError
        : style.dealerInput

    const dealerIdErrorMessage = dealerIdError
        ? (<div className={style.dealerInputErrorMessage}>{t(`error.fieldIsRequired`)}</div>)
        : null

    const content = isLoading ? (
        <div className={style.spinnerContainer}>
            <Spinner />
        </div>
    ) : urlLink ? (
        <div className={style.linkContent}>
            <div className={style.urlLink}>{urlLink}</div>
            <div className={style.urlButton}>
                <button title={t(`digitalGiveaway.linkCopy`)} onClick={copyClickHandler} className={`${style.urlCopyButton} ${getCopiedClass()}`}>
                    {getCopiedIcon()}
                </button>
            </div>
            <div className={style.info}>
                {t(`digitalGiveaway.info`)}
            </div>
            <div className={style.qrCodeContainer}>
                <QRCode
                    value={urlLink}
                    size={200}
                    level="M"
                    className={style.qrCode}
                />
            </div>
        </div>
    )
        : (
            <div className={style.mainContent}>
                <div className={style.dealerWrapper}>
                    <Input
                        value={dealerId}
                        onChange={(event) => setDealerId(event.target.value)}
                        minLength={9}
                        placeholder={t(`digitalGiveaway.modalGenerateDealerId`)}
                        className={dealerIdClasses}
                        data-testid="input-digital-giveaway-dealer-id"
                    />
                    {dealerIdErrorMessage}
                </div>
                <button
                    className={style.button}
                    onClick={() => clickHandler(true)}>
                    {t(`digitalGiveaway.modalGenerateWith`)}
                </button>
            </div>
        )

    const headerContent = urlLink ?
        t(`digitalGiveaway.modalLoadedHeader`)
        : t(`digitalGiveaway.modalGenerateHeader`)

    return (
        <Modal isVisible={modalIsOpen} onClose={closeHandler}>
            <ModalClosingButton onClick={closeHandler} />
            <section className={style.container}>
                <h2 className={style.headline}>{headerContent}</h2>
                {content}
            </section>
        </Modal>
    )
}

export default DigitalGiveawayModal
